import { z } from 'zod';
import { formValueSchema } from './form-validation.js';
import { fieldKeySchema } from './forms.js';
import { dateRangeQuerySchema, paginationQuerySchema } from './pagination.js';
/**
 * Absolute ceiling on photos per project visit. The server enforces a lower,
 * env-configurable limit (`MAX_PHOTOS_PER_VISIT`, default 6) on top of this.
 */
export const MAX_ATTACHMENTS_PER_VISIT = 10;
/** Additional project visits one trip may carry (besides the main visit's own project). */
export const MAX_ADDITIONAL_PROJECTS = 9;
/** Absolute ceiling on photos in one trip (one Zoho ticket). */
export const MAX_ATTACHMENTS_PER_TRIP = MAX_ATTACHMENTS_PER_VISIT * (MAX_ADDITIONAL_PROJECTS + 1);
/**
 * Absolute ceiling per photo. The server enforces `MAX_PHOTO_BYTES` (default
 * 3 MB) on top of this; the app targets 200-400 KB (STANDARD) or 500-900 KB
 * (HIGH) after compression.
 */
export const MAX_ATTACHMENT_BYTES = 3 * 1024 * 1024;
export const ALLOWED_ATTACHMENT_MIME_TYPES = ['image/jpeg', 'image/png'];
export const geoFixSchema = z
    .object({
    latitude: z.number().min(-90).max(90),
    longitude: z.number().min(-180).max(180),
    accuracyMeters: z.number().nonnegative().max(1_000_000).nullable().default(null),
    /** Android reports whether the fix came from a mock-location provider. */
    mocked: z.boolean().nullable().default(null),
    capturedAt: z.iso.datetime({ offset: true }).nullable().default(null),
})
    .refine((fix) => !(fix.latitude === 0 && fix.longitude === 0), {
    message: '0,0 is not a valid location; send status UNAVAILABLE instead',
});
export const LOCATION_CAPTURE_STATUSES = ['OK', 'PERMISSION_DENIED', 'UNAVAILABLE', 'TIMEOUT'];
export const locationCaptureSchema = z
    .object({
    status: z.enum(LOCATION_CAPTURE_STATUSES),
    fix: geoFixSchema.nullable(),
})
    .refine((capture) => (capture.status === 'OK') === (capture.fix !== null), {
    message: 'fix must be present exactly when status is OK',
    path: ['fix'],
});
/** Where the agent stood when reporting a branch that is not in the list. */
export const reportedPointSchema = z.object({
    latitude: z.number().min(-90).max(90),
    longitude: z.number().min(-180).max(180),
    accuracyMeters: z.number().min(0).max(100_000).nullable(),
});
/**
 * The branch of a visit: an official one from the catalog, or one the agent
 * typed because it is not listed (the visit goes ahead; an unverified-branch
 * alert is raised for review).
 *
 * UNLISTED names the merchant either by id (it is in the list) or as typed text
 * (it is not); exactly one of `brandId` and `manualBrandName`. Every field
 * added after the first release is optional, so older app builds stay valid.
 */
export const branchSelectionSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('LISTED'), branchId: z.uuid() }),
    z
        .object({
        kind: z.literal('UNLISTED'),
        brandId: z.uuid().nullable().default(null),
        manualBrandName: z.string().trim().min(2).max(120).nullable().default(null),
        manualBranchName: z.string().trim().min(2).max(120),
        addressNote: z.string().trim().max(300).nullable().default(null),
        agentNote: z.string().trim().max(500).nullable().default(null),
        reportedPoint: reportedPointSchema.nullable().default(null),
    })
        .refine((s) => (s.brandId === null) !== (s.manualBrandName === null), {
        message: 'Give either a merchant from the list or a typed merchant name',
        path: ['manualBrandName'],
    }),
]);
const safeFileName = z
    .string()
    .trim()
    .min(1)
    .max(200)
    .refine((name) => !/[/\\:*?"<>|]/.test(name) && ![...name].some((ch) => ch.charCodeAt(0) < 0x20), 'Invalid file name');
/** Most typed ("not in the list") entries one visit may carry, over all fields. */
export const MAX_MANUAL_OPTIONS = 10;
/**
 * An entry typed by the agent because it is not in a multi-select field's list
 * (e.g. a project). The visit goes ahead with it; the server raises a review item.
 */
export const manualOptionSchema = z.object({
    fieldKey: z.string().trim().min(1).max(60),
    name: z.string().trim().min(2).max(100),
});
export const attachmentManifestItemSchema = z.object({
    id: z.uuid(),
    fieldKey: fieldKeySchema,
    fileName: safeFileName,
    mime: z.enum(ALLOWED_ATTACHMENT_MIME_TYPES),
    size: z.number().int().min(1).max(MAX_ATTACHMENT_BYTES),
    /**
     * The additional project visit this photo belongs to (its `blockId`). Absent
     * or null: the main visit's own project block. Older app builds never send it.
     */
    blockId: z.uuid().nullable().optional(),
});
/**
 * An additional project visit in the same trip: only the per-project answers.
 * Branch-level answers (merchant, branch, manager...) are asked once, on the main visit.
 */
export const additionalProjectSchema = z.object({
    /** Generated on the phone when the card is added; photos point at it. */
    blockId: z.uuid(),
    formData: z.record(z.string().max(60), formValueSchema),
    manualOptions: z.array(manualOptionSchema).max(MAX_MANUAL_OPTIONS).default([]),
});
export const visitSubmissionSchema = z
    .object({
    submissionId: z.uuid({ version: 'v4' }),
    formVersionId: z.uuid(),
    startedAt: z.iso.datetime({ offset: true }),
    endedAt: z.iso.datetime({ offset: true }),
    /** Device clock when the submission was created; used to estimate clock skew. */
    deviceSubmittedAt: z.iso.datetime({ offset: true }),
    startLocation: locationCaptureSchema,
    endLocation: locationCaptureSchema,
    branchSelection: branchSelectionSchema.nullable(),
    /** Branch-level answers plus the main visit's own project block. */
    formData: z.record(z.string().max(60), formValueSchema),
    attachments: z.array(attachmentManifestItemSchema).max(MAX_ATTACHMENTS_PER_TRIP).default([]),
    appVersion: z.string().max(40).nullable().default(null),
    /** Entries typed because they are not in a MULTI_SELECT field's list (optional: older app builds omit it). */
    manualOptions: z.array(manualOptionSchema).max(MAX_MANUAL_OPTIONS).default([]),
    /** Additional project visits of the same trip (optional: older app builds and one-project visits omit it). */
    additionalProjects: z.array(additionalProjectSchema).max(MAX_ADDITIONAL_PROJECTS).default([]),
    /**
     * The assignment this visit was started from, if any. Never a precondition:
     * an unknown, cancelled or someone else's assignment is ignored, never a rejection.
     */
    assignmentId: z.uuid().nullable().optional(),
    /**
     * The shift the visit was started in (D110). The server rejects a check-in
     * outside every shift; optional only for visits queued by older app builds.
     */
    clientShiftId: z.uuid().nullable().optional(),
})
    .refine((v) => new Set(v.attachments.map((a) => a.id)).size === v.attachments.length, {
    message: 'Attachment ids must be unique',
    path: ['attachments'],
})
    .refine((v) => new Set(v.additionalProjects.map((p) => p.blockId)).size === v.additionalProjects.length, {
    message: 'Project block ids must be unique',
    path: ['additionalProjects'],
})
    .refine((v) => v.attachments.every((a) => !a.blockId || v.additionalProjects.some((p) => p.blockId === a.blockId)), {
    message: 'A photo points at a project block that is not in this submission',
    path: ['attachments'],
});
export const VISIT_STATUSES = ['RECEIVED', 'PROCESSING', 'SYNCED', 'PARTIALLY_SYNCED', 'FAILED'];
/**
 * Life of one photo, mirrored on the phone and in Postgres. The bytes never
 * rest on our server: `UPLOADING` is the moment the phone's stream is being
 * piped into Zoho, and `UPLOADED` means Zoho confirmed it with an attachment id
 * (the only state in which the phone may delete its cached copy).
 */
export const ATTACHMENT_STATUSES = ['PENDING', 'UPLOADING', 'UPLOADED', 'FAILED'];
/**
 * Result of reading the ticket's attachments back from Zoho after an upload:
 * VERIFIED listed with the same size · NOT_LISTED Zoho gave an id the ticket does
 * not list · SIZE_MISMATCH listed with another size · UNVERIFIED the read-back
 * could not run (the upload itself returned a valid id).
 */
export const ATTACHMENT_VERIFICATIONS = ['VERIFIED', 'NOT_LISTED', 'SIZE_MISMATCH', 'UNVERIFIED'];
export const visitStatusResponseSchema = z.object({
    submissionId: z.string(),
    status: z.enum(VISIT_STATUSES),
    zohoTicketNumber: z.string().nullable(),
    receivedAt: z.string(),
    syncedAt: z.string().nullable(),
    lastError: z.string().nullable(),
    attachments: z.array(z.object({
        id: z.string(),
        fieldKey: z.string(),
        status: z.enum(ATTACHMENT_STATUSES),
        /** Present exactly when Zoho confirmed the photo; the phone deletes its copy on this. */
        zohoAttachmentId: z.string().nullable().default(null),
        attempts: z.number().int().default(0),
        error: z.string().nullable(),
        /** Read-back from the Zoho ticket after upload (D58). Null until checked; optional for older servers. */
        verification: z.enum(ATTACHMENT_VERIFICATIONS).nullable().default(null),
    })),
});
export const myPerformanceQuerySchema = paginationQuerySchema
    .extend({ pageSize: z.coerce.number().int().min(1).max(50).default(20) })
    .and(dateRangeQuerySchema);
export const myPerformanceItemSchema = z.object({
    submissionId: z.string(),
    status: z.enum(VISIT_STATUSES),
    startedAt: z.string(),
    endedAt: z.string(),
    durationMinutes: z.number(),
    brandName: z.string().nullable(),
    branchName: z.string().nullable(),
    isUnlistedBranch: z.boolean(),
    zohoTicketNumber: z.string().nullable(),
    /** Live Zoho status when available (cached); null if unknown. */
    zohoTicketStatus: z.string().nullable(),
    isAnomaly: z.boolean(),
});
export const myPerformanceResponseSchema = z.object({
    items: z.array(myPerformanceItemSchema),
    total: z.number().int(),
    page: z.number().int(),
    pageSize: z.number().int(),
    summary: z.object({
        totalVisits: z.number().int(),
        syncedVisits: z.number().int(),
        anomalyCount: z.number().int(),
        avgDurationMinutes: z.number().nullable(),
    }),
});
export const ANOMALY_REASON_CODES = [
    'START_OUTSIDE_GEOFENCE',
    'END_OUTSIDE_GEOFENCE',
    'LOW_ACCURACY',
    'MOCK_LOCATION',
    'LOCATION_MISSING',
    'BRANCH_LOCATION_UNVERIFIED',
    'UNLISTED_BRANCH',
    'DURATION_TOO_SHORT',
    'DURATION_TOO_LONG',
    'CLOCK_SKEW',
    'OVERLAPPING_VISIT',
];
export const anomalyReasonSchema = z.object({
    code: z.enum(ANOMALY_REASON_CODES),
    weight: z.number().int(),
    detail: z.string(),
});
export const anomalyThresholdsSchema = z.object({
    /** Fixes with worse accuracy are ignored for distance checks and flagged. */
    maxAccuracyMeters: z.number().int().min(5).max(5000),
    minDurationMinutes: z.number().min(0).max(600),
    maxDurationMinutes: z
        .number()
        .min(1)
        .max(24 * 60),
    maxClockSkewMinutes: z
        .number()
        .min(1)
        .max(24 * 60),
    /** A visit is an anomaly when its score reaches this value. */
    scoreThreshold: z.number().int().min(1).max(1000),
    weights: z.record(z.enum(ANOMALY_REASON_CODES), z.number().int().min(0).max(100)),
});
export const DEFAULT_ANOMALY_THRESHOLDS = {
    maxAccuracyMeters: 100,
    minDurationMinutes: 3,
    maxDurationMinutes: 8 * 60,
    maxClockSkewMinutes: 10,
    scoreThreshold: 30,
    weights: {
        START_OUTSIDE_GEOFENCE: 30,
        END_OUTSIDE_GEOFENCE: 30,
        LOW_ACCURACY: 10,
        MOCK_LOCATION: 60,
        LOCATION_MISSING: 30,
        BRANCH_LOCATION_UNVERIFIED: 5,
        UNLISTED_BRANCH: 10,
        DURATION_TOO_SHORT: 20,
        DURATION_TOO_LONG: 15,
        CLOCK_SKEW: 20,
        OVERLAPPING_VISIT: 40,
    },
};
/** Server-side limits that are not admin-tunable anomaly thresholds. */
export const VISIT_TIMING_LIMITS = {
    /** Reject timestamps further than this into the future (relative to server AND device clock). */
    maxFutureSkewMinutes: 15,
    /** Reject visits whose duration exceeds this outright (anything shorter may still be flagged). */
    hardMaxDurationHours: 24,
};
/**
 * Header carrying the device clock at the moment an HTTP request is sent. Used to
 * measure clock skew (the submission body may have been queued offline for days).
 */
export const DEVICE_TIME_HEADER = 'x-device-time';
//# sourceMappingURL=visits.js.map